import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useFarm } from "@/lib/farmContext";
import { useDispatches } from "@/lib/repo";
import { exportDispatchesCsv, exportDispatchesPdf } from "@/lib/dispatchExport";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Truck, Download, FileText, Search } from "lucide-react";
import { formatDate } from "@/lib/utils";

const TYPE_OPTIONS = [
  { value: "all", label: "Todos los tipos" },
  { value: "sale_slaughterhouse", label: "Venta a matadero" },
  { value: "sale_breeding", label: "Venta para cría" },
  { value: "slaughter", label: "Sacrificio en finca" },
  { value: "transfer_out", label: "Salida de traslado" },
];

function periodToRange(period) {
  const now = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  if (period === "month") return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: null };
  if (period === "3months") return { from: iso(new Date(now.getFullYear(), now.getMonth() - 2, 1)), to: null };
  if (period === "year") return { from: iso(new Date(now.getFullYear(), 0, 1)), to: null };
  return { from: null, to: null };
}

export default function DispatchesReport() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { farms } = useFarm();
  const [period, setPeriod] = useState("month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [farmId, setFarmId] = useState("all");
  const [type, setType] = useState("all");
  const [destQuery, setDestQuery] = useState("");
  const [detail, setDetail] = useState(null);

  const assignedFarmIds = user?.role === "handler" ? (user?.assigned_farm_ids || []) : undefined;
  const range = period === "custom" ? { from: customFrom || null, to: customTo || null } : periodToRange(period);

  const items = useDispatches({
    assignedFarmIds,
    farmId: farmId !== "all" ? farmId : undefined,
    type: type !== "all" ? type : undefined,
    destinationQuery: destQuery || undefined,
    dateFrom: range.from || undefined,
    dateTo: range.to || undefined,
  });

  const farmName = (id) => farms.find((f) => f.id === id)?.name || "—";

  const totals = useMemo(() => {
    const byType = {};
    let animals = 0, weight = 0, amount = 0, hasAmount = false;
    items.forEach((i) => {
      animals += i.animal_count;
      weight += i.total_weight || 0;
      if (i.amount != null) { amount += i.amount; hasAmount = true; }
      byType[i.type] = (byType[i.type] || 0) + i.animal_count;
    });
    return { animals, weight: Math.round(weight * 100) / 100, amount: hasAmount ? Math.round(amount * 100) / 100 : null, byType };
  }, [items]);

  return (
    <div data-testid="dispatches-report-page" className="space-y-4 max-w-3xl mx-auto">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="h-9 w-9"><ArrowLeft size={18} /></Button>
        <div className="min-w-0 flex-1">
          <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-muted-foreground">Informe de egresos</div>
          <h1 className="font-editorial text-[26px] sm:text-[32px] leading-[1.05] tracking-tight">Ventas y despachos</h1>
        </div>
        <div className="flex gap-1.5 shrink-0">
          <Button variant="outline" size="sm" onClick={() => exportDispatchesCsv(items, farmName)} disabled={items.length === 0} data-testid="dispatches-export-csv">
            <Download size={13} className="mr-1" />CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportDispatchesPdf(items, farmName)} disabled={items.length === 0} data-testid="dispatches-export-pdf">
            <FileText size={13} className="mr-1" />PDF
          </Button>
        </div>
      </div>

      {/* Totales del período */}
      <Card data-testid="dispatches-totals">
        <CardContent className="p-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div>
            <div className="text-[10.5px] font-mono uppercase tracking-[0.12em] text-muted-foreground">Animales salidos</div>
            <div className="font-editorial text-[26px] tabular-nums mt-0.5" data-testid="dispatches-total-animals">{totals.animals}</div>
          </div>
          <div>
            <div className="text-[10.5px] font-mono uppercase tracking-[0.12em] text-muted-foreground">Kilos totales</div>
            <div className="font-editorial text-[26px] tabular-nums mt-0.5" data-testid="dispatches-total-weight">{totals.weight || 0}</div>
          </div>
          {totals.amount != null && (
            <div>
              <div className="text-[10.5px] font-mono uppercase tracking-[0.12em] text-muted-foreground">Importe total</div>
              <div className="font-editorial text-[26px] tabular-nums mt-0.5" data-testid="dispatches-total-amount">$ {totals.amount}</div>
            </div>
          )}
          <div className="col-span-2 sm:col-span-3 flex flex-wrap gap-1.5 pt-1">
            {Object.entries(totals.byType).map(([t, c]) => (
              <Badge key={t} variant="outline" className="text-[11px]">{TYPE_OPTIONS.find((o) => o.value === t)?.label || t}: {c}</Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2" data-testid="dispatches-filters">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="h-9 w-auto min-w-[150px]" data-testid="dispatches-period-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="month">Mes actual</SelectItem>
            <SelectItem value="3months">Últimos 3 meses</SelectItem>
            <SelectItem value="year">Año en curso</SelectItem>
            <SelectItem value="all">Todas las fechas</SelectItem>
            <SelectItem value="custom">Rango personalizado</SelectItem>
          </SelectContent>
        </Select>

        {period === "custom" && (
          <>
            <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="h-9 w-auto" data-testid="dispatches-date-from" />
            <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="h-9 w-auto" data-testid="dispatches-date-to" />
          </>
        )}

        <Select value={farmId} onValueChange={setFarmId}>
          <SelectTrigger className="h-9 w-auto min-w-[140px]" data-testid="dispatches-farm-filter"><SelectValue placeholder="Finca" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las fincas</SelectItem>
            {farms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="h-9 w-auto min-w-[150px]" data-testid="dispatches-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>

        <div className="relative flex-1 min-w-[140px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={destQuery} onChange={(e) => setDestQuery(e.target.value)} placeholder="Destino / comprador…" className="h-9 pl-9" data-testid="dispatches-dest-filter" />
        </div>
      </div>

      {items.length === 0 && (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">
          No hay despachos registrados en este filtro.
        </CardContent></Card>
      )}

      {items.length > 0 && (
        <Card>
          <CardContent className="p-0 divide-y divide-border">
            {items.map((i) => (
              <button key={i.id} onClick={() => setDetail(i)} data-testid={`dispatch-row-${i.id}`}
                className="w-full p-3 flex items-center gap-3 text-sm hover:bg-accent/30 transition-colors text-left">
                <div className="w-9 h-9 rounded-full bg-[hsl(var(--brand-ochre)/0.16)] text-[hsl(var(--brand-ochre))] grid place-items-center shrink-0">
                  <Truck size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium flex items-center gap-1.5 flex-wrap">
                    {i.type_label}
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">{formatDate(i.date)}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {i.destination} · {farmName(i.farm_id)}
                    {i.guide_number && <> · Guía {i.guide_number}</>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-editorial text-[18px] tabular-nums">{i.animal_count}</div>
                  <div className="text-[11px] font-mono text-muted-foreground">{i.total_weight ? `${i.total_weight} kg` : "—"}</div>
                </div>
              </button>
            ))}
          </CardContent>
        </Card>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="dispatch-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">{detail.type_label}</DialogTitle>
              </DialogHeader>
              <div className="space-y-1 text-[13px]">
                <Row k="Fecha" v={formatDate(detail.date)} />
                <Row k="Destino / comprador" v={detail.destination} />
                <Row k="Finca de origen" v={farmName(detail.farm_id)} />
                {detail.guide_number && <Row k="Guía INSAI" v={detail.guide_number} />}
                {detail.guide_date && <Row k="Fecha guía" v={formatDate(detail.guide_date)} />}
                {detail.driver && <Row k="Chofer" v={`${detail.driver}${detail.driver_id ? ` (C.I. ${detail.driver_id})` : ""}`} />}
                {detail.plate && <Row k="Placa" v={detail.plate} />}
                {detail.registered_by_name && <Row k="Registrado por" v={detail.registered_by_name} />}
                {detail.amount != null && <Row k="Importe" v={`$ ${detail.amount}`} />}
              </div>
              <div className="rounded-[var(--radius)] border border-border overflow-hidden mt-2">
                <div className="divide-y divide-border max-h-[35vh] overflow-y-auto" data-testid="dispatch-detail-animals">
                  {detail.animals.map((a) => (
                    <div key={a.id} className="px-3 py-2 flex items-center justify-between gap-2 text-[12.5px]">
                      <div className="min-w-0">
                        <div className="font-mono truncate">{a.chip_code || "—"}</div>
                        <div className="text-muted-foreground text-[11px]">Lomo {a.lomo_number || "—"} · {a.category || "—"}</div>
                      </div>
                      <div className="font-mono text-muted-foreground shrink-0">{a.weight_kg != null ? `${a.weight_kg} kg` : "—"}</div>
                    </div>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => exportDispatchesPdf([detail], farmName)} data-testid="dispatch-detail-export-pdf">
                  <FileText size={14} className="mr-1.5" /> Descargar PDF (constancia)
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground">{k}</span>
      <span className="text-foreground font-medium text-right">{v}</span>
    </div>
  );
}
